const GENERIC_LOCAL_PARTS = new Set([
  'info',
  'hello',
  'contact',
  'sales',
  'support',
  'admin',
  'office',
  'team',
  'help',
  'mail',
  'marketing',
  'orders',
  'service',
  'reception',
  'frontdesk',
  'customerservice',
]);

const ROLE_LOCAL_PARTS = new Set([
  'accounting',
  'hr',
  'billing',
  'invoices',
  'careers',
  'jobs',
  'press',
  'media',
  'legal',
  'compliance',
]);

export function normalizeEmail(email: string | null | undefined): string | null {
  const trimmed = email?.trim().toLowerCase();
  if (!trimmed || !trimmed.includes('@')) return null;
  return trimmed;
}

export function normalizeName(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[—–]/g, '-');
}

export function normalizeAccountKey(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeState(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const lower = trimmed.toLowerCase();
  const map: Record<string, string> = {
    il: 'IL',
    illinois: 'IL',
    'new york': 'NY',
    california: 'CA',
    michigan: 'MI',
    ohio: 'OH',
    massachusetts: 'MA',
    'new jersey': 'NJ',
    colorado: 'CO',
    florida: 'FL',
    oregon: 'OR',
    washington: 'WA',
    arizona: 'AZ',
    pennsylvania: 'PA',
    maryland: 'MD',
    nevada: 'NV',
    connecticut: 'CT',
    missouri: 'MO',
    'rhode island': 'RI',
    vermont: 'VT',
    'new hampshire': 'NH',
    maine: 'ME',
    arkansas: 'AR',
    oklahoma: 'OK',
  };
  return map[lower] ?? trimmed.slice(0, 2).toUpperCase();
}

/** Values that may appear in vault `state` for a canonical US state code. */
export function equivalentStateValues(state: string | null | undefined): string[] {
  const normalized = normalizeState(state);
  if (!normalized) return [];
  const fullNames: Record<string, string> = {
    IL: 'Illinois',
    NY: 'New York',
    CA: 'California',
    MI: 'Michigan',
    OH: 'Ohio',
    MA: 'Massachusetts',
    NJ: 'New Jersey',
    CO: 'Colorado',
    FL: 'Florida',
  };
  const full = fullNames[normalized];
  return full ? [normalized, full] : [normalized];
}

export function statesEquivalent(
  left: string | null | undefined,
  right: string | null | undefined
): boolean {
  if (!left || !right) return true;
  return normalizeState(left) === normalizeState(right);
}

export function buildContactPerson(first: string | null, last: string | null): string | null {
  const parts = [first, last].filter((part) => part && part !== '-').map((part) => part!.trim());
  return parts.length > 0 ? parts.join(' ') : null;
}

export function classifyEmailTier(
  email: string | null,
  firstName: string | null,
  lastName: string | null
): 'named' | 'role' | 'generic' | 'unknown' {
  const normalized = normalizeEmail(email);
  if (!normalized) return 'unknown';
  const local = normalized.split('@')[0]?.replace(/[.+]/g, '') ?? '';
  if (GENERIC_LOCAL_PARTS.has(local)) return 'generic';
  if (ROLE_LOCAL_PARTS.has(local)) return 'role';
  const hasName = Boolean(firstName?.trim() && firstName !== '-') || Boolean(lastName?.trim() && lastName !== '-');
  if (hasName) return 'named';
  if (/^[a-z]+[._-]?[a-z]+$/i.test(local) || local.includes('.')) return 'named';
  return 'role';
}

export function scoreContactQuality(row: {
  email: string | null;
  email_tier: ReturnType<typeof classifyEmailTier>;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  title: string | null;
  account_name: string | null;
}): number {
  let score = 0;
  if (row.email) score += 4;
  if (row.email_tier === 'named') score += 4;
  else if (row.email_tier === 'role') score += 2;
  else if (row.email_tier === 'generic') score += 1;
  if (row.first_name && row.first_name !== '-') score += 1;
  if (row.last_name && row.last_name !== '-') score += 1;
  if (row.phone) score += 1;
  if (row.title) score += 1;
  if (row.account_name) score += 1;
  return score;
}

export function shouldSkipFile(relativePath: string, headers: string[]): { skip: boolean; reason?: string } {
  const lowerPath = relativePath.toLowerCase();
  if (lowerPath.includes('test--') || lowerPath.includes('/test')) {
    return { skip: true, reason: 'test export' };
  }
  if (lowerPath.includes('active campaign') || lowerPath.includes('for active campaign')) {
    return { skip: true, reason: 'activecampaign merge artifact' };
  }
  const joined = headers.join(' ').toLowerCase();
  if (joined.includes('dm sent') && joined.includes('phase summary')) {
    return { skip: true, reason: 'campaign tracking sheet' };
  }
  if (headers[0]?.startsWith('Business-Location-Export-') && headers.length === 1) {
    return { skip: true, reason: 'malformed export header' };
  }
  return { skip: false };
}
